import { newUuid } from '../platform';
import {
  PROTOCOL_VERSION,
  type ClientEventType,
  type ClientFrame,
  type ServerFrame,
} from '../types/websocket';

export function createCommand<TPayload extends Record<string, unknown>>(
  type: ClientEventType,
  payload: TPayload,
  requestId: string = newUuid(),
): ClientFrame<TPayload> {
  return { version: PROTOCOL_VERSION, type, request_id: requestId, payload };
}

/**
 * Validate a frame before anything is allowed to act on it.
 *
 * A malformed frame is a protocol violation, not a message to interpret
 * leniently: the connection is closed rather than guessed at, because a client
 * that improvises around unexpected shapes is how state silently diverges.
 */
export function parseServerFrame(raw: unknown): ServerFrame {
  if (typeof raw !== 'string') {
    throw new TypeError('Realtime frame must be text.');
  }
  const value: unknown = JSON.parse(raw);
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError('Realtime frame must be an object.');
  }
  const frame = value as Record<string, unknown>;
  if (frame.version !== PROTOCOL_VERSION || typeof frame.type !== 'string') {
    throw new TypeError('Unsupported realtime envelope.');
  }
  if (!frame.payload || typeof frame.payload !== 'object' || Array.isArray(frame.payload)) {
    throw new TypeError('Realtime payload must be an object.');
  }
  return frame as unknown as ServerFrame;
}

/** Durable events carry a positive sequence; ephemeral ones carry zero. */
export function isDurableEvent(frame: ServerFrame): boolean {
  const sequence = (frame as { sequence?: unknown }).sequence;
  return typeof sequence === 'number' && Number.isInteger(sequence) && sequence > 0;
}
